import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Button, Avatar, Badge, Switch } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, InboxOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
// Import the useGetLogo hook from the appearance controller
import { useGetLogo } from "@/shared/api/client/appearance-controller";
// Keeping this as fallback
const defaultLogo = '/images/hazina-logo.jpg';
import { useMenu } from "@/hooks/menus/use-menu";
import getMenu from "@/components/sidenav/menu-converter";
import Logout from "@/views/auth/Logout";
import { useAuth } from "@/hooks/auth/use-auth";
import { useTheme } from "@/components/common/ThemeProvider";
import { MenuTheme } from "antd/es/menu/MenuContext";
import { NotificationBell } from "@/modules/in-app-notifications";
import { useGetVersion } from "@/shared/api/client/version-controller";

const { Sider, Content } = Layout;

// Define sidebar width constants
const SIDEBAR_WIDTH = 220; // Reduced from 256px
const SIDEBAR_COLLAPSED_WIDTH = 80;

const MenuOnLeftLayout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { dark } = useTheme();
    const [theme, setTheme] = useState<MenuTheme>(dark ? 'dark' : 'light');
    const { menus } = useMenu();
    const { isLoading, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Get the logo from the appearance controller
    const { data: logoData } = useGetLogo();
    // Use the dynamic logo or fall back to the default
    const logoSrc = typeof logoData === 'string' ? logoData : logoData?.data || defaultLogo;

    const { data: versionData } = useGetVersion();

    useEffect(() => {
        setTheme(dark ? 'dark' : 'light');
    }, [dark]);

    // Separate the window resize check from the collapsed state dependency
    useEffect(() => {
        const checkIfMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            
            // Auto-collapse menu only on initial load or window resize
            if (mobile) {
                setCollapsed(true);
            }
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        return () => {
            window.removeEventListener('resize', checkIfMobile);
        };
    }, []); // Empty dependency array so it only runs on mount and unmount

    const toggleCollapsed = useCallback(() => {
        setCollapsed(prevState => !prevState);
    }, []);

    // Handle menu item click
    const handleMenuClick = useCallback(({ key, domEvent }: { key: string, domEvent: React.MouseEvent }) => {
        // If on mobile, collapse the menu after clicking an item
        if (isMobile) {
            setCollapsed(true);
        }
    }, [isMobile]);

    const bottomMenuItems = [
        {
            key: 'profile',
            icon: <Avatar size="small" src={user?.profilePath} icon={<UserOutlined />} />,
            label: (isLoading) ? "... " : user?.fullname,
            children: [
                { key: 'view-profile', label: <Link to={"/user/profile"}>View Profile</Link> },
                { key: 'change-password', label: <Link to={"/user/change-password"}><LockOutlined /> Change Password</Link> },
                { key: 'logout', label: <Logout /> },
            ],
        },
    ];

    // Find the current selected key based on the location pathname
    const findSelectedKey = () => {
        const currentPath = location.pathname;
        
        // Flatten all menu items to search through them
        const flattenMenus = (menuItems: any[]): any[] => {
            return menuItems.reduce((acc, item) => {
                if (item.children) {
                    return [...acc, item, ...flattenMenus(item.children)];
                }
                return [...acc, item];
            }, []);
        };
        
        // Add defensive check to ensure menus is an array before using map
        const menuArray = Array.isArray(menus) ? menus : [];
        const allMenuItems = flattenMenus(menuArray.map(menu => getMenu(menu)).filter(Boolean));
        
        // Find the menu item that matches the current path
        const currentMenuItem = allMenuItems.find(item => {
            if (!item || !item.key) return false;
            // Check if the current path starts with the menu item's key (which should be the path)
            return currentPath.startsWith(item.key);
        });
        
        return currentMenuItem ? [currentMenuItem.key] : [];
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                theme={theme}
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={SIDEBAR_WIDTH}
                breakpoint="lg"
                collapsedWidth={isMobile ? 0 : SIDEBAR_COLLAPSED_WIDTH}
                onBreakpoint={(broken) => {
                    // Don't automatically set collapsed here, as it interferes with user toggle
                    // We'll handle this in the useEffect
                }}
                className={isMobile ? "fixed z-10 h-full shadow-lg" : ""}
                style={{ 
                    transition: 'all 0.2s',
                    height: '100vh',
                    position: isMobile ? 'fixed' : 'sticky',
                    top: 0,
                    left: collapsed && isMobile ? -SIDEBAR_COLLAPSED_WIDTH : 0,
                    zIndex: 1001,
                    overflow: 'hidden',
                }}
            >
                <div className="logo" style={{ 
                    height: 32, 
                    margin: '16px', 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'flex-start' 
                }}>
                    <img src={logoSrc} alt="Scale Fi Logo" style={{ height: '100%' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 64px)' }}>
                    <Menu
                        className="mt-4"
                        theme={theme}
                        mode="inline"
                        selectedKeys={findSelectedKey()}
                        // @ts-ignore
                        onClick={handleMenuClick}
                        items={(Array.isArray(menus) ? menus : []).map((menu) => getMenu(menu)).filter(Boolean)}
                        style={{ flex: 1, overflowY: 'auto' }}
                    />
                    <div style={{ marginTop: 'auto' }}>
                        <Menu
                            theme={theme}
                            mode="inline"
                            selectable={false}
                            // @ts-ignore
                            onClick={handleMenuClick}
                            items={bottomMenuItems}
                        />
                        {!collapsed && (
                            <div style={{
                                padding: '8px 12px',
                                textAlign: 'center',
                                fontSize: 10,
                                opacity: 0.5,
                                lineHeight: '1.4',
                                color: theme === 'dark' ? '#ffffff' : '#000000',
                            }}>
                                <div>v{versionData?.version || 'dev-local'}</div>
                                <div>
                                    Powered by{' '}
                                    <a href={versionData?.website || 'https://scalefi.co.tz'} target="_blank" rel="noopener noreferrer"
                                       style={{ color: 'inherit', textDecoration: 'underline' }}>
                                        {versionData?.poweredBy || 'ScaleFi'}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Sider>
            <Layout style={{ 
                marginLeft: isMobile ? 0 : 0, 
                transition: 'margin-left 0.2s'
            }}>
                <Content style={{ 
                    position: 'relative', 
                    backgroundColor: dark ? 'rgb(17 24 39 / var(--tw-bg-opacity))' : '#f0f2f5',
                    minHeight: '100vh'
                }}>
                    <div style={{ 
                        position: 'fixed', 
                        top: 8, 
                        left: isMobile ? 8 : (collapsed ? SIDEBAR_COLLAPSED_WIDTH + 8 : SIDEBAR_WIDTH + 8), 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        width: 120, 
                        zIndex: 1000, 
                        transition: 'left 0.2s'
                    }}>
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={toggleCollapsed}
                            style={{
                                fontSize: '16px',
                                width: 48,
                                height: 48,
                                color: dark ? '#ffffff' : '#001529',
                                backgroundColor: dark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.8)',
                                border: 'none',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                        />
                        <NotificationBell />
                    </div>
                    {/* Overlay for mobile when sidebar is open */}
                    {!collapsed && isMobile && (
                        <div 
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                zIndex: 1000
                            }}
                            onClick={toggleCollapsed}
                        />
                    )}
                    <div style={{ 
                        paddingTop: 64, 
                        paddingLeft: isMobile ? 16 : (collapsed ? 24 : 32), 
                        paddingRight: isMobile ? 16 : 32,
                        paddingBottom: 24,
                        maxWidth: isMobile ? '100%' : '1600px',
                        margin: '0 auto'
                    }}>
                        <Outlet />
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

// Wrap the Layout with QueryClientProvider
const AppWithQueryClient = () => (
        <MenuOnLeftLayout />
);

export default AppWithQueryClient;